import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoriesAPI } from '@/services/api/categories.api';
import { localizedField } from '@/utils/localizedField';
import type { Category } from '@/types/category.types';

/**
 * Resuelve el slug de categoría de una historia al nombre localizado del
 * catálogo (`Story.category` guarda el slug; el alumno debe ver el nombre).
 *
 * Carga el catálogo público (solo activas) una vez y lo cachea a nivel de módulo
 * para que todas las pantallas compartan el mismo fetch.
 */
let cache: Category[] | null = null;
let inflight: Promise<Category[]> | null = null;

function loadCategories(): Promise<Category[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = categoriesAPI
      .list()
      .then((items) => {
        cache = items;
        return items;
      })
      .catch((err) => {
        inflight = null; // permite reintentar en el próximo montaje
        throw err;
      });
  }
  return inflight;
}

export function useCategoryLabels() {
  const { i18n, t } = useTranslation();
  const [cats, setCats] = useState<Category[]>(cache ?? []);

  useEffect(() => {
    if (cache) return;
    let active = true;
    loadCategories()
      .then((items) => {
        if (active) setCats(items);
      })
      .catch(() => {
        /* fallback a t('common.general') si no carga */
      });
    return () => {
      active = false;
    };
  }, []);

  const bySlug = new Map(cats.map((c) => [c.slug, c]));

  /** Nombre localizado de la categoría; fallback a "General" si no resuelve. */
  const labelFor = (slug: string | null | undefined): string => {
    if (!slug) return t('common.general');
    const cat = bySlug.get(slug);
    return cat ? localizedField(cat, 'name', i18n.language) : t('common.general');
  };

  /** Opciones {value: slug, label: nombre localizado} para los Select del backoffice. */
  const categoryOptions = cats
    .slice()
    .map((c) => ({ value: c.slug, label: localizedField(c, 'name', i18n.language) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { labelFor, categoryOptions };
}
